import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EMPTY, Observable, ReplaySubject, catchError, map, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ImageLoaderProvider } from '../../osp-ui/services/image-loader-provider.service';
import { DocumentLink } from '../contract/document-link.contract';
import { FileUtils } from '../utils/file-utils';
import { AuthService } from '../../core/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class FileDownloadService extends ImageLoaderProvider {
  private baseUrl = environment.APP_FILE_DOWNLOAD_SERVICE_BASE_URL;
  private imageMap: Record<string, Observable<SafeUrl>> = {};

  constructor(
    private httpClient: HttpClient,
    private domSanitizer: DomSanitizer,
    private authService: AuthService
  ) {
    super();
  }

  getImage(imageKey: string, customerId: string | undefined): Observable<SafeUrl> {
    imageKey = `${FileUtils.isFromGlobalDirectory(imageKey) ? 'global/' : ''}${imageKey}`;

    if (this.imageMap[imageKey]) {
      return this.imageMap[imageKey];
    }

    const subj = new ReplaySubject<SafeUrl>(1);
    this.imageMap[imageKey] = subj.asObservable();

    this.getFileBlobData(imageKey, customerId)
      .pipe(
        take(1),
        map(blobData => this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blobData))),
        catchError(error => {
          subj.error(error);
          return EMPTY;
        })
      )
      .subscribe(data => subj.next(data));

    return this.imageMap[imageKey];
  }

  saveFile({ documentKey, fileName }: DocumentLink, customerId?: string | undefined): void {
    const encodedDocumentKey = FileUtils.encodeDocumentKey(documentKey);
    this.getFileBlobData(encodedDocumentKey, customerId).subscribe(blobData => {
      const safeUrl: string = this.domSanitizer.sanitize(
        SecurityContext.URL,
        this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blobData))
      ) ?? '';
      const link = document.createElement('a');
      link.href = safeUrl;
      link.download = fileName || 'img';
      link.dispatchEvent(new MouseEvent(`click`, { bubbles: true, cancelable: true, view: window }));
      URL.revokeObjectURL(safeUrl);
      link.remove();
    });
  }

  private getFileBlobData(imageKey: string, customerId: string | undefined): Observable<Blob> {
    let headers: HttpHeaders = new HttpHeaders();
    if (!!customerId && this.shouldOverrideCustomerId(customerId)) {
      headers = headers.set('x-customer-id', customerId);
    }
    return this.httpClient.get(`${this.baseUrl}/${imageKey}`, { responseType: 'blob', headers });
  }

  private shouldOverrideCustomerId(customerId: string | undefined): boolean {
    return customerId !== this.authService.userCustomerId;
  }
}
